// ============================================
// 数据绑定 Zustand Store
// 管理数据源注册、连接状态、绑定配置、实时数据
// ============================================

import { create } from 'zustand';
import { DatasourceManager } from '../core/DatasourceManager';
import { BindingEngine } from '../core/BindingEngine';
import useSceneStore from './sceneStore';
import type {
  RuntimeDatasource,
  BindingEvaluationResult,
} from '../types/databinding';
import type {
  DatasourceConfig,
  DataBinding,
  SceneDocument,
} from '../types/designer';

const DEBUG = true;
const log = (...args: any[]) => DEBUG && console.log('[DataBinding]', ...args);

interface DataBindingState {
  datasources: DatasourceConfig[];
  runtimeStates: Record<string, RuntimeDatasource>;
  bindings: Record<string, DataBinding[]>;
  liveData: Record<string, any>;
  lastEvaluations: BindingEvaluationResult[];
  selectedDatasourceId: string | null;
  selectedNodeId: string | null;
  isPanelOpen: boolean;
  datasourceManager: DatasourceManager;
  bindingEngine: BindingEngine;

  initDocument: (doc: SceneDocument) => void;
  addDatasource: (config: DatasourceConfig) => void;
  removeDatasource: (id: string) => void;
  updateDatasource: (id: string, config: Partial<DatasourceConfig>) => void;
  connectDatasource: (id: string) => Promise<void>;
  disconnectDatasource: (id: string) => void;
  addBinding: (nodeId: string, binding: DataBinding) => void;
  removeBinding: (nodeId: string, bindingId: string) => void;
  updateBinding: (nodeId: string, bindingId: string, updates: Partial<DataBinding>) => void;
  getNodeBindings: (nodeId: string) => DataBinding[];
  processDataUpdate: (datasourceId: string, data: any) => BindingEvaluationResult[];
  setSelectedDatasource: (id: string | null) => void;
  setSelectedNode: (id: string | null) => void;
  togglePanel: () => void;
  setPanelOpen: (open: boolean) => void;
  getLiveData: (datasourceId: string) => any;
  getAllRuntimeStates: () => RuntimeDatasource[];
}

const datasourceManager = new DatasourceManager();
const bindingEngine = new BindingEngine();

export const useDataBindingStore = create<DataBindingState>((set, get) => ({
  datasources: [],
  runtimeStates: {},
  bindings: {},
  liveData: {},
  lastEvaluations: [],
  selectedDatasourceId: null,
  selectedNodeId: null,
  isPanelOpen: false,
  datasourceManager,
  bindingEngine,

  initDocument: (doc: SceneDocument) => {
    const datasources = doc.datasources || [];
    log('initDocument: datasources=', datasources.length, 'nodes=', doc.nodes?.length);
    set({ datasources });
    bindingEngine.loadDocument(doc);

    for (const ds of datasources) {
      try {
        datasourceManager.register(ds);
      } catch (err) {
        console.warn(`Failed to register datasource ${ds.id}:`, err);
      }
    }

    // 同步文档中的绑定到 BindingEngine
    for (const node of doc.nodes || []) {
      if (node.bindings) {
        for (const binding of node.bindings) {
          bindingEngine.registerBinding(node.id, binding);
        }
      }
    }

    const runtimeStates: Record<string, RuntimeDatasource> = {};
    for (const ds of datasources) {
      runtimeStates[ds.id] = {
        id: ds.id, config: ds, status: 'disconnected', lastUpdate: 0, dataCount: 0,
      };
    }
    set({ runtimeStates });
  },

  addDatasource: (config: DatasourceConfig) => {
    log('addDatasource:', config.id, config.name, config.type);
    datasourceManager.register(config);
    // 2026-09-27（审计 FE-F08）：数据源随场景保存 → 标记未保存（否则用户无感知，刷新丢失）
    (useSceneStore as any).getState?.()?.setDirty?.(true);

    set((state) => ({
      datasources: [...state.datasources, config],
      runtimeStates: {
        ...state.runtimeStates,
        [config.id]: {
          id: config.id, config, status: 'disconnected', lastUpdate: 0, dataCount: 0,
        },
      },
    }));
  },

  removeDatasource: (id: string) => {
    log('removeDatasource:', id);
    datasourceManager.unregister(id);
    (useSceneStore as any).getState?.()?.setDirty?.(true);
    set((state) => {
      const { [id]: _, ...restRuntime } = state.runtimeStates;
      const { [id]: __, ...restLive } = state.liveData;
      const { [id]: ___, ...restBindings } = state.bindings;
      return {
        datasources: state.datasources.filter((ds) => ds.id !== id),
        runtimeStates: restRuntime, liveData: restLive, bindings: restBindings,
        selectedDatasourceId: state.selectedDatasourceId === id ? null : state.selectedDatasourceId,
      };
    });
  },

  updateDatasource: (id: string, updates: Partial<DatasourceConfig>) => {
    (useSceneStore as any).getState?.()?.setDirty?.(true);
    set((state) => ({
      datasources: state.datasources.map((ds) => ds.id === id ? { ...ds, ...updates } : ds),
    }));
  },

  connectDatasource: async (id: string) => {
    log('connectDatasource:', id);
    try {
      set((state) => ({
        runtimeStates: {
          ...state.runtimeStates,
          [id]: { ...state.runtimeStates[id], status: 'connecting' },
        },
      }));

      await datasourceManager.connect(id);

      set((state) => ({
        runtimeStates: {
          ...state.runtimeStates,
          [id]: { ...state.runtimeStates[id], status: 'connected', lastUpdate: Date.now() },
        },
      }));
      log('connectDatasource: connected', id);
    } catch (err: any) {
      set((state) => ({
        runtimeStates: {
          ...state.runtimeStates,
          [id]: { ...state.runtimeStates[id], status: 'error', lastError: err.message },
        },
      }));
      log('connectDatasource: error', id, err.message);
      // 2026-09-14 修复（FE-56）：向上抛出让面板正确提示失败（原吞掉异常导致失败也弹"连接成功"）
      throw err;
    }
  },

  disconnectDatasource: (id: string) => {
    log('disconnectDatasource:', id);

    // 找到所有绑定到此数据源的节点ID
    const state = get();
    const affectedNodeIds: string[] = [];
    for (const [nodeId, bindings] of Object.entries(state.bindings)) {
      for (const binding of bindings) {
        if (binding.datasource === id) {
          affectedNodeIds.push(nodeId);
          break;
        }
      }
    }

    // 先发重置事件，让 Three.js 恢复原始颜色
    if (affectedNodeIds.length > 0) {
      datasourceManager.emit('binding:reset', affectedNodeIds);
    }

    datasourceManager.disconnect(id);
    set((state) => ({
      runtimeStates: {
        ...state.runtimeStates,
        [id]: { ...state.runtimeStates[id], status: 'disconnected' },
      },
    }));
  },

  addBinding: (nodeId: string, binding: DataBinding) => {
    log('addBinding: nodeId=', nodeId, 'binding=', binding.id, 'property=', binding.property, 'datasource=', binding.datasource);

    // 同步到 BindingEngine
    bindingEngine.registerBinding(nodeId, binding);
    log('addBinding: registered in BindingEngine. Total bindings:', bindingEngine.getStats().bindingCount);
    (useSceneStore as any).getState?.()?.setDirty?.(true);

    set((state) => ({
      bindings: {
        ...state.bindings,
        [nodeId]: [...(state.bindings[nodeId] || []), binding],
      },
    }));
  },

  removeBinding: (nodeId: string, bindingId: string) => {
    log('removeBinding:', bindingId);
    bindingEngine.unregisterBinding(bindingId);
    (useSceneStore as any).getState?.()?.setDirty?.(true);
    set((state) => {
      const existing = state.bindings[nodeId] || [];
      const updated = existing.filter((b) => b.id !== bindingId);
      const newBindings = { ...state.bindings };
      if (updated.length === 0) { delete newBindings[nodeId]; }
      else { newBindings[nodeId] = updated; }
      return { bindings: newBindings };
    });
  },

  updateBinding: (nodeId: string, bindingId: string, updates: Partial<DataBinding>) => {
    (useSceneStore as any).getState?.()?.setDirty?.(true);
    set((state) => ({
      bindings: {
        ...state.bindings,
        [nodeId]: (state.bindings[nodeId] || []).map((b) => b.id === bindingId ? { ...b, ...updates } : b),
      },
    }));
  },

  getNodeBindings: (nodeId: string) => {
    return get().bindings[nodeId] || [];
  },

  processDataUpdate: (datasourceId: string, data: any) => {
    log('processDataUpdate:', datasourceId, data);
    set((state) => ({
      liveData: { ...state.liveData, [datasourceId]: data },
    }));
    const results = bindingEngine.processDataUpdate(datasourceId, data);
    log('processDataUpdate: results=', results.length, results.map(r => `${r.bindingId}:${r.success?'ok':'fail'}=${r.value}`));
    set({ lastEvaluations: results });
    return results;
  },

  setSelectedDatasource: (id) => set({ selectedDatasourceId: id }),
  setSelectedNode: (id) => set({ selectedNodeId: id }),
  togglePanel: () => set((s) => ({ isPanelOpen: !s.isPanelOpen })),
  setPanelOpen: (open) => set({ isPanelOpen: open }),
  getLiveData: (datasourceId) => get().liveData[datasourceId],
  getAllRuntimeStates: () => Object.values(get().runtimeStates),
}));

// ============================================
// 全局事件监听
// ============================================

datasourceManager.on('data:update', ({ datasourceId, data }: any) => {
  log('event:data:update', datasourceId, data);

  const state = useDataBindingStore.getState();
  useDataBindingStore.setState({
    liveData: { ...state.liveData, [datasourceId]: data },
  });

  // 直接从 store 的 bindings 中查找关联到此数据源的绑定
  const allBindings = state.bindings;
  const results: BindingEvaluationResult[] = [];

  for (const [nodeId, nodeBindings] of Object.entries(allBindings)) {
    for (const binding of nodeBindings) {
      if (binding.datasource !== datasourceId) continue;

      // 直接评估表达式（不依赖 BindingEngine）
      let value: any = data;
      const expr = (binding.expression || '').trim();

      if (expr) {
        // data.xxx 路径引用
        if (expr.startsWith('data.')) {
          const path = expr.slice(5);
          const parts = path.split('.');
          let current: any = data;
          for (const p of parts) {
            if (current == null) break;
            current = current[p];
          }
          value = current;
          log(`  evaluate: ${expr} -> path=${path} -> ${JSON.stringify(value)}`);
        }
        // 模板表达式 {{...}}
        else if (expr.startsWith('{{') && expr.endsWith('}}')) {
          try {
            const fn = new Function('data', `return ${expr.slice(2, -2)}`);
            value = fn(data);
          } catch { value = data; }
          log(`  evaluate: ${expr} -> ${JSON.stringify(value)}`);
        }
        // 其他
        else {
          value = data;
        }
      }

      results.push({
        bindingId: binding.id,
        nodeId,
        property: binding.property,
        value,
        success: true,
        timestamp: Date.now(),
      });
    }
  }

  log('event:data:update: binding results=', results.length);
  results.forEach(r => {
    log(`  -> ${r.bindingId}: nodeId=${r.nodeId} prop=${r.property} val=${JSON.stringify(r.value)} ok=${r.success}`);
  });

  useDataBindingStore.setState({ lastEvaluations: results });

  if (results.length > 0) {
    log('event:data:update: emitting binding:apply with', results.length, 'results');
    datasourceManager.emit('binding:apply', results);
  } else {
    log('event:data:update: NO bindings found for datasource', datasourceId);
  }
});

datasourceManager.on('datasource:status', ({ id, status }: any) => {
  log('event:datasource:status', id, status);
  const state = useDataBindingStore.getState();
  const current = state.runtimeStates[id];
  if (current) {
    useDataBindingStore.setState({
      runtimeStates: {
        ...state.runtimeStates,
        [id]: { ...current, status, lastUpdate: Date.now() },
      },
    });
  }
});

datasourceManager.on('datasource:error', ({ id, error }: any) => {
  log('event:datasource:error', id, error);
  // 2026-09-14 修复（FE-56）：错误事件写入 runtimeStates（原仅打日志，适配器置 error 传不到 UI）
  const state = useDataBindingStore.getState();
  const current = state.runtimeStates[id];
  if (current) {
    useDataBindingStore.setState({
      runtimeStates: {
        ...state.runtimeStates,
        [id]: { ...current, status: 'error', lastError: String(error?.message || error || '') },
      },
    });
  }
});
